import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useRouter } from "components/hooks";
import { useContext } from "react";
import * as yup from "yup";
import { LoaderContext } from "components/context/LoaderContext";
import { toast } from "react-toastify";
import { useQuery } from "@tanstack/react-query";
import { publishMemento } from "apis/magnets";
import { getPollEndpoint, getCreatePoll } from "apis/poll";
import { getBrandMagnet } from "apis/brand";
import PollForm from "components/organisms/poll/PollForm";
import Loading from "components/atoms/Loading";

import { get, patch, post, remove } from "utils/request";
import { flattenObj } from "utils/HelperFunctions";

import moment from "moment";
import { ROUTES, TOAST_CONFIG } from "../../constants";

const pollSchema = yup.object().shape({
  name: yup.string().trim().required("* Required field"),
  questions: yup.array().of(
    yup.object().shape({
      question: yup.string().required("* Required field"),
      choices: yup.array().of(
        yup.object().shape({
          text: yup.string().required("* Required field"),
        }),
      ),
    }),
  ),
  start_time: yup.date().min(new Date(), "Start time cannot be in the past").required("* Required field"),
  end_time: yup
    .date()
    .nullable()
    .when("start_time", (start, schema) => (start ? schema.min(start, "End time must be after start time") : schema))
    .required("* Required field"),
  viewer_type: yup.number().min(0).max(1),
});

function Poll({
  pollDefaultValues,
  isEditForm,
  isPublished,
  refetchPollData,
}: {
  pollDefaultValues: PollFormDataNew;
  isEditForm: boolean;
  isPublished: boolean;
  refetchPollData: () => void;
}) {
  const formMethods = useForm<PollFormDataNew>({
    resolver: yupResolver(pollSchema),
    defaultValues: pollDefaultValues,
    mode: "onChange",
  });

  const router = useRouter();
  const { setLoading } = useContext(LoaderContext);
  const {
    handleSubmit,
    formState: { isDirty },
    setError,
  } = formMethods;

  const { magnetId, pollId, brandId }: { magnetId: string; pollId: string; brandId: string } = router.query;

  const handleGetMagnetFailure = (errorMessage: string, error: any) => {
    toast.error(errorMessage, TOAST_CONFIG);
    if (error.response.status === 404) {
      router.go(`/${ROUTES.NOT_FOUND}`);
      return;
    }
    throw new Error(errorMessage);
  };

  const navigateToPreviousPage = () => router.go(-1);

  const getMagnetDetails = () =>
    get(getBrandMagnet(brandId, magnetId), {
      processError: handleGetMagnetFailure,
    });

  const {
    isLoading: isMagnetDetailsLoading,
    error: magnetDetailsError,
    data: magnetDetails,
  } = useQuery({
    queryKey: [brandId, magnetId],
    queryFn: getMagnetDetails,
  });

  const handlePollError = (errorMessage: string, error: any, errorData: any) => {
    setLoading(false);
    toast.error(errorMessage, TOAST_CONFIG);
    if (error.response.status === 404) {
      toast.error("Server Responded with 404", TOAST_CONFIG);
    }
    if (error.response.status === 500) {
      toast.error("Request failed with status 500", TOAST_CONFIG);
    }

    // flatening object to get the absolute key to get errors
    const flatenErrorObject = flattenObj(errorData.error);

    Object.keys(flatenErrorObject).forEach((errorKey) => {
      if (errorKey.includes("choices.")) {
        const choiceIndex = errorKey.split(".")[1];
        // @ts-ignore
        setError(`choices.${choiceIndex}.choice`, {
          type: "custom",
          message: flatenErrorObject[errorKey] as string,
        });
        return;
      }
      // @ts-ignore
      setError(errorKey, {
        type: "custom",
        message: flatenErrorObject[errorKey] as string,
      });
    });

    throw new Error(errorMessage);
  };

  const handlePublishPollSuccess = () => {
    toast.success("Poll published successfully", TOAST_CONFIG);
    setLoading(false);
    navigateToPreviousPage();
  };

  const handlePublishPoll = async (pollIdToPublish: string) =>
    post(
      publishMemento(magnetId, pollIdToPublish),
      {},
      {
        processData: handlePublishPollSuccess,
        processError: handlePollError,
      },
    );

  const handleUpdatePollSuccess = () => {
    toast.success("Poll Updated Successfully", TOAST_CONFIG);
    setLoading(false);
    navigateToPreviousPage();
  };

  const handleUpdatePollAndTriggerPublishPoll = async (payload: PollData, isPublishButtonClicked?: boolean) =>
    patch(getPollEndpoint(magnetId, pollId), payload, {
      processData: () => (isPublishButtonClicked ? handlePublishPoll(pollId) : handleUpdatePollSuccess()),
      processError: handlePollError,
    });

  const handleCreatePollSuccess = () => {
    toast.success("Poll save as draft successfully", TOAST_CONFIG);
    setLoading(false);
    navigateToPreviousPage();
  };

  const handleCreatePollAndTriggerPublishPoll = async (payload: PollData, isPublishButtonClicked?: boolean) =>
    post(getCreatePoll(magnetId), payload, {
      processData: ({
        data,
      }: {
        data: {
          id: string;
        };
      }) => (isPublishButtonClicked && data ? handlePublishPoll(data.id) : handleCreatePollSuccess()),
      processError: handlePollError,
    });

  const transformFormDataToPollPayload = (formData: PollFormDataNew): PollData => ({
    // converting choices: array of objects to array of string choices.
    ...formData,
    start_time: moment(formData.start_time).valueOf(),
    end_time: moment(formData.end_time).valueOf(),
    question: formData.questions.map((questionObj) => questionObj.question),
    choices: formData.questions.map((questionObj) => questionObj.choices.map((choiceObj) => choiceObj.text)),
  });

  const handleDeletePollSuccess = () => {
    toast.success("Poll Deleted successfully", TOAST_CONFIG);
    setLoading(false);
    navigateToPreviousPage();
  };

  const handleDeletePoll = () => {
    setLoading(true);
    remove(getPollEndpoint(magnetId, pollId), {
      processData: handleDeletePollSuccess,
      processError: handlePollError,
    });
  };

  const handleSaveAsDraftOrUpdateButtonClick = (pollData: PollFormDataNew) => {
    setLoading(true);
    if (isEditForm) {
      handleUpdatePollAndTriggerPublishPoll(transformFormDataToPollPayload(pollData));
    } else {
      handleCreatePollAndTriggerPublishPoll(transformFormDataToPollPayload(pollData));
    }
  };

  const handlePublishPollButtonClick = async (pollData: PollFormDataNew) => {
    setLoading(true);
    if (isEditForm) {
      // if default values have changed, then only updated the poll
      // else publish
      if (isDirty) {
        handleUpdatePollAndTriggerPublishPoll(transformFormDataToPollPayload(pollData), true);
      } else handlePublishPoll(pollId);
    } else {
      handleCreatePollAndTriggerPublishPoll(transformFormDataToPollPayload(pollData), true);
    }
  };

  if (isMagnetDetailsLoading) return <Loading />;
  if (magnetDetailsError) return <p>Unable to fetch Magnet details</p>;

  return (
    <FormProvider
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...formMethods}
    >
      <PollForm
        refetchPollData={refetchPollData}
        handlePublishPoll={handlePublishPollButtonClick}
        handleSaveAsDraftOrUpdatePoll={handleSaveAsDraftOrUpdateButtonClick}
        handleSubmit={handleSubmit}
        handleDeletePoll={handleDeletePoll}
        magnetImageUrl={magnetDetails.image_url}
        magnetName={magnetDetails.name}
        brandName={magnetDetails.brand_name}
        isEditPollForm={isEditForm}
        isPollPublished={isPublished}
      />
    </FormProvider>
  );
}
export default Poll;
